// @flow

import {
  compact,
  flow,
  get,
  keys,
  identity,
  keyBy,
  isEmpty,
  isNil,
  groupBy,
  omitBy,
  pickBy,
  toString,
  values
} from 'lodash/fp';

import * as challengesApi from '../api/challenges';
import * as types from '../constants/actionTypes';
import lineUpSelector from '../selectors/lineUpSelector';
import { PRIZES, CHALLENGE, GAMES, OPPONENT_ENTRY } from '../constants/apiTypes';
import selectionToPost from '../selectors/selectionToPost';
import {
  getRandomPlayersAndTeams,
  getSelectionAndBonus,
  convertFantasyInsiderPosition,
  convertPositionForFantasyInsider
} from '../utils/challengeUtils';
import pxcTransferDataToPost from '../selectors/pxcSelector';
import { crunchTeams } from '../scenes/fantasyInsider/api';

type GetState = () => GlobalState;

const omitNil = omitBy(x => (Array.isArray(x) ? isEmpty(x) : isNil(x)));
const truthyKeys = flow(pickBy(identity), keys);

function buildChallengeQuery(state, vals = {}) {
  const opponentEntryId = vals.opponentEntryId || get('id', state.entry.opponentEntry);
  const { userEntryId } = vals;

  return omitNil({
    embed: truthyKeys({
      ads: state.settings.ads,
      current_user_entry: true,
      'games.teams': true,
      'games.players': true,
      opponent_entry: opponentEntryId,
      rule_set: true
    }),
    user_entry_id: userEntryId,
    opponent_entry: opponentEntryId
  });
}

export function fetchChallenge(
  id: string,
  options: { force?: boolean, quiet?: boolean, userEntryId?: string } = {}
) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const query = buildChallengeQuery(state, {
      userEntryId: options.userEntryId
    });
    dispatch({
      type: types.API_CALL,
      api: CHALLENGE,
      force: !!options.force,
      id,
      quiet: !!options.quiet,
      request: challengesApi.getChallenge(id, query),
      timestamp: Date.now()
    });
  };
}

export function pollChallenge() {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    dispatch({
      type: types.START_API_POLL,
      key: CHALLENGE,
      duration: getState().challenge.pollDuration,
      getApiCall: () => {
        const state = getState();
        const { id } = state.challenge;
        return {
          api: CHALLENGE,
          id,
          quiet: true,
          request: () => {
            const pollQuery = buildChallengeQuery(getState());
            return challengesApi.getChallenge(id, pollQuery);
          }
        };
      }
    });
  };
}

export function fetchOpponentEntry(id: string) {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    const state = getState();
    const challengeId = state.challenge.id;

    const query = buildChallengeQuery(state, { opponentEntryId: id });
    dispatch({
      type: types.API_CALL,
      api: OPPONENT_ENTRY,
      request: challengesApi.getChallenge(challengeId, query)
    });
    // restart the poll now
    pollChallenge();
  };
}

export function stopPollingChallenge() {
  return {
    type: types.STOP_API_POLL,
    key: CHALLENGE
  };
}

export function setPollDuration(duration) {
  return {
    type: types.SET_POLL_DURATION,
    duration
  };
}

export function fetchPrizes(challengeId, page) {
  return {
    type: types.API_CALL,
    api: PRIZES,
    request: challengesApi.getPrizes(challengeId, { page })
  };
}

export function fetchGames(challengeId) {
  return {
    type: types.API_CALL,
    api: GAMES,
    request: challengesApi.getGames(challengeId)
  };
}

export function resetChallenge() {
  return {
    type: types.RESET_CHALLENGE
  };
}

export function resetLineUpErrors() {
  return {
    type: types.RESET_LINEUP_ERRORS
  };
}

export function setPositionFilter(position) {
  return {
    type: types.SET_POSITION_FILTER,
    position
  };
}

export function togglePositionFilter(position, exclusive = false) {
  return {
    type: types.TOGGLE_POSITION_FILTER,
    position,
    exclusive
  };
}

export function setGameFilter(game) {
  return {
    type: types.SET_GAME_FILTER,
    game
  };
}

export function addEntity(entity, position) {
  return {
    type: types.ADD_ENTITY,
    entity,
    position
  };
}

export function removeEntity(entity, position, switchPosition = false) {
  return {
    type: types.REMOVE_ENTITY,
    entity,
    position,
    switchPosition
  };
}

export function switchSelectedPosition(entity, position, newPosition) {
  return {
    type: types.SWITCH_SELECTED_POSITION,
    entity,
    position,
    newPosition
  };
}

export function resetProjections() {
  return {
    type: types.RESET_FANTASY_INSIDER_PROJECTIONS
  };
}

export function updateEntityProperty(entity, field, value) {
  return {
    type: types.UPDATE_ENTITY_PROPERTY,
    entity,
    field,
    value
  };
}

export function updatePlayers(players) {
  return {
    type: types.UPDATE_PLAYERS,
    players
  };
}

export function toggleEntityProperty(entity, field) {
  return {
    type: types.TOGGLE_ENTITY_PROPERTY,
    entity,
    field
  };
}

export function lockAll() {
  return {
    type: types.LOCK_ALL
  };
}

export function unlockAll() {
  return {
    type: types.UNLOCK_ALL
  };
}

export function lockEntity(entity, position) {
  return {
    type: types.LOCK_ENTITY,
    entity,
    position
  };
}

export function unlockEntity(entity, position) {
  return {
    type: types.UNLOCK_ENTITY,
    entity,
    position
  };
}

export function addBonus(key, playerId) {
  return {
    type: types.ADD_BONUS,
    key,
    playerId
  };
}

export function selectBonus(key) {
  return {
    type: types.SELECT_BONUS,
    key
  };
}

export function removeBonus(key) {
  return {
    type: types.REMOVE_BONUS,
    key
  };
}

export function autoFillLineUp() {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    const state = getState();

    try {
      // getRandomPlayersAndTeams can throw an error if it can't find all the needed selections to fill the lineup
      const selections = getRandomPlayersAndTeams(
        values(state.challenge.players),
        values(state.challenge.teams),
        state.challenge.playerPositions,
        lineUpSelector(state).filter(p => p.locked),
        state.challenge.playerQuota,
        state.challenge.teamQuota,
        state.challenge.playersPerSideCap,
        state.challenge.playersPerGame,
        state.challenge.salaryCap,
        state.challenge.shareQuota,
        state.challenge.enableSmartLineUp
      );

      dispatch({
        type: types.SET_LINE_UP,
        autofill: true,
        lock: false,
        selections
      });
    } catch (e) {
      alert('Unable to fill the lineup');
    }
  };
}

export function smartFillLineUp() {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    const state = getState();
    const { salaryCap, players, playerPositions, playersPerSideCap } = state.challenge;
    const { settings, players: insiderPlayers } = state.fantasyInsider;
    const { selections: lineUpSelections } = state.lineUp || {};

    const positions = keyBy('abbr', playerPositions);

    crunchTeams({
      salaryCap,
      minSalary: settings.minSalary,
      UniquePlayers: settings.uniquePlayers,
      Platform: 'DraftStars',
      StackTeam: '',
      StackNumber: settings.numPlayersForTeamStacking,
      portmethod: settings.portfolioMethod,
      ExposurePct: 1,
      MinCeiling: settings.minCeiling,
      MaxPlayers: playersPerSideCap,
      RandomSD: 1,
      Lineups: 1,
      LineupList: Object.values(players)
        .map(p => {
          const insiderPlayer = get(p.idStr, insiderPlayers);

          if (!insiderPlayer) {
            return undefined;
          }

          let locked = 0;
          let activePos;

          Object.entries(lineUpSelections).forEach(([posId, sels]) => {
            if (get(`${p.idStr}.locked`, sels)) {
              locked = 1;
              activePos = convertPositionForFantasyInsider(playerPositions[posId].abbr);
            }
          });

          return {
            MaxExposure: settings.maxExposurePerPlayer,
            Name: insiderPlayer.insiderName,
            playerID: p.id,
            Position: insiderPlayer.insiderPosition,
            Position2: insiderPlayer.insiderPosition2,
            Price: p.salary,
            activePos,
            Rating: insiderPlayer.insiderScore,
            Status: insiderPlayer.insiderStatus,
            StdDev: insiderPlayer.insiderStdDev,
            Team: insiderPlayer.insiderTeam,
            warning: insiderPlayer.insiderWarning ? 1 : 0,
            Lock: locked
          };
        })
        .filter(p => p && (!get(`${p.id}.isQuestionable`, players) || p.Lock))
    })
      .then(({ data }) => {
        if (!data || !Array.isArray(data.Lineups) || data.Lineups.length === 0) {
          dispatch({
            type: types.SMART_LINEUP_FAILED
          });
          return;
        }

        const lineup = get('[0].Lineup', data.Lineups) || [];

        const selections = compact(
          lineup.map(({ playerID, Position }) => {
            const player = Object.values(players).find(p => p.id === toString(playerID));

            const position = convertFantasyInsiderPosition(Position);

            return player && get(`${position}.id`, positions)
              ? {
                  positionId: get(`${position}.id`, positions),
                  idStr: player.idStr,
                  isTeam: false
                }
              : null;
          })
        );

        if (selections.length === lineup.length) {
          dispatch({
            type: types.SET_LINE_UP,
            autofill: false,
            lock: false,
            selections: groupBy('positionId', selections),
            smartProjections: get('[0].Score', data.Lineups)
          });
          return;
        }
        dispatch({
          type: types.SMART_LINEUP_FAILED
        });
      })
      .catch(() => {
        // console.log(err)
      });
  };
}

function updateEntryWithApiFunc(apiFunc) {
  return (entryId: string) => (dispatch: Dispatch<*>, getState: GetState) => {
    const state = getState();

    const selection = selectionToPost(state);

    dispatch({
      type: types.UPDATE_CHALLENGE_REQUEST
    });

    apiFunc(entryId || state.entry.myEntry.id, { selection }).then(
      res => {
        dispatch({
          type: types.UPDATE_CHALLENGE_SUCCESS,
          data: res.data
        });
      },
      payload => {
        dispatch({
          type: types.UPDATE_CHALLENGE_FAILURE,
          payload
        });
      }
    );
  };
}

export const updateEntry = updateEntryWithApiFunc(challengesApi.updateEntry);

export const updateTrainingEntry = updateEntryWithApiFunc(challengesApi.updateTrainingEntry);

export function bulkUpdate(entryIdsToUpdate = []) {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    const state = getState();

    const selection = selectionToPost(state);

    dispatch({
      type: types.UPDATE_CHALLENGE_REQUEST
    });

    challengesApi.bulkUpdate({ entries: entryIdsToUpdate, selection }).then(
      res => {
        dispatch({
          type: types.UPDATE_CHALLENGE_SUCCESS,
          data: res.data
        });
      },
      payload => {
        dispatch({
          type: types.UPDATE_CHALLENGE_FAILURE,
          payload
        });
      }
    );
  };
}

export function submitEntryWithApiFunc(apiFunc) {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    const state = getState();

    const selection = selectionToPost(state);

    dispatch({
      type: types.SUBMIT_CHALLENGE_REQUEST
    });

    apiFunc(state.challenge.id, { selection }).then(
      res => {
        dispatch({
          type: types.SUBMIT_CHALLENGE_SUCCESS,
          data: res.data,
          meta: res.meta
        });
      },
      payload => {
        dispatch({
          type: types.SUBMIT_CHALLENGE_FAILURE,
          payload
        });
      }
    );
  };
}

export function submitEntry() {
  return submitEntryWithApiFunc(challengesApi.submitEntry);
}

export function submitPxcEntries({ batchId, transactions }) {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    const state = getState();

    const selection = selectionToPost(state);
    const pxcTransferData =
      batchId && transactions
        ? pxcTransferDataToPost({ batchId, transactions })
        : {
            challenges: [
              {
                id: state.challenge.id,
                quantity: 1
              }
            ]
          };

    dispatch({
      type: types.SUBMIT_CHALLENGE_REQUEST
    });

    challengesApi.submitPxcEntries({ ...pxcTransferData, selection }).then(
      res => {
        dispatch({
          type: types.SUBMIT_CHALLENGE_SUCCESS,
          data: res.data[0],
          meta: res.meta
        });
      },
      payload => {
        dispatch({
          type: types.SUBMIT_CHALLENGE_FAILURE,
          payload
        });
      }
    );
  };
}

export function submitTrainingEntry() {
  return submitEntryWithApiFunc(challengesApi.submitTrainingEntry);
}

export function loadLineUp(selectionHash: string) {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    const state = getState();
    const lineupToLoad =
      get('myTeams.lineup.selectionHash', state) === selectionHash
        ? state.myTeams.lineup
        : state.myTeams.lineups.find(lineup => lineup.selectionHash === selectionHash);

    const { selections, bonuses } = getSelectionAndBonus(lineupToLoad.selection);

    dispatch({
      type: types.SET_LINE_UP,
      loadLineUp: true,
      lock: true,
      bonuses,
      selections
    });
  };
}

export function resetLineUp() {
  return {
    type: types.RESET_LINEUP
  };
}

export function shareChallenge(data) {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    const { challenge } = getState();

    dispatch({
      type: types.SHARE_CHALLENGE_REQUEST
    });

    challengesApi.shareChallenge(challenge.id, data).then(
      res => {
        dispatch({
          type: types.SHARE_CHALLENGE_SUCCESS,
          data: res.data
        });
      },
      res => {
        dispatch({
          type: types.SHARE_CHALLENGE_FAILURE,
          errors: res.errors
        });
      }
    );
  };
}

export function resetPerformanceDiff() {
  return {
    type: types.RESET_PERFORMANCE_DIFF
  };
}

export function setPerformanceDiff(value) {
  return {
    type: types.SET_PERFORMANCE_DIFF,
    value
  };
}

export function resetOpponentEntry() {
  return {
    type: types.RESET_OPPONENT_ENTRY
  };
}

export function filterEntryStatus(value) {
  return {
    type: types.FILTER_ENTRY_STATUS,
    value
  };
}
